import {Form, Table} from 'antd';
import {FormInstance} from 'antd/es/form';
import {TableProps} from 'antd/es/table';
import {Key, TablePaginationConfig, TableRowSelection} from 'antd/es/table/interface';
import cn from 'classnames';
import {isEqual} from 'lodash-es';
import React, {FC, memo, useCallback, useContext, useEffect, useMemo, useState} from 'react';

import {FormsFileModalContext} from 'components/documents/file-modal/file-modal.context';
import {IEditableCellProps, TEditableColumn} from 'components/editable-table';
import {useEditableTablePagination} from 'components/editable-table/hooks/use-editable-table-pagination';
import {useRowValidation} from 'components/editable-table/hooks/use-validate-row';
import {calculateFixedColumnLeft} from 'components/editable-table/lib';
import {EDITABLE_TABLE_LIST_NAME} from 'components/editable-table/model/constants';
import {EditableFormTableContext} from 'components/editable-table/model/editable-form-table.context';
import {EditableCell} from 'components/editable-table/ui/editable-cell';
import {TableModeContext} from 'components/table/context';
import {convertFlatObjectToTableState} from 'components/table/utils/table.utils';
import {IFieldMeta, ITableEntityMeta} from 'entities/metadata';
import {TEntity, TEntityValue} from 'modules/data';
import {IDefaultTableStateFlat, ITableEntityData} from 'modules/data/data-types';
import {convertWidthData} from 'shared/utils/convert-width-data';
import {useAppSelector} from 'store/config/hooks';
import {selectDocumentsPagination} from 'store/slices/documents-uploading-slice';

import 'components/table/table.less';
import s from './editable-table.module.less';

interface IEditableTableProps extends Exclude<TableProps<any>, ['columns']> {
    columns: TEditableColumn[];
    handleSave: (row: TEntity) => void;
    entityName?: string;
    params?: IDefaultTableStateFlat;
    // Для использования пагинации без метаданных
    isPageJumperEnabled?: boolean;
    isPageSizeChangerEnabled?: boolean;
    disabledPagination?: boolean;
    // Данные для работы с метой
    tableData?: ITableEntityData;
    rowSelection?: TableRowSelection<Record<string, TEntity[]>>;
    onChangeTable?: (
        pagination: TablePaginationConfig,
        filters: Record<string, Key[] | null>,
        sorter: any,
        url: string,
    ) => void;
    url?: string;
    metadata?: ITableEntityMeta;
    loading?: boolean;
    isSelectable?: boolean;
    linkField?: string;
    dependentLinkField?: string;
    dataSource?: TEntity[];
    // Используется для модалок загрузки файлов т.к. они хранят файлы со всех страниц
    isIndexRelative?: boolean;
}

interface IHiddenFormProps {
    form: FormInstance;
    formListName: string;
    data: TEntity[];
}

// Скрытая форма для управления данными
const HiddenForm: FC<IHiddenFormProps> = ({
    form,
    formListName,
    data,
}: IHiddenFormProps) => {
    useEffect(() => {
        if (data && form) {
            form.setFieldsValue({
                [formListName]: data
            });
        }
    }, [data, form]);

    return (
        <Form
            form={form}
            initialValues={{[formListName]: data}}
            component={false}
            style={{ display: 'none' }}
        >
            <Form.List name={formListName}>
                {() => null}
            </Form.List>
        </Form>
    );
};

interface IEditableTableContentProps {
    columns: TEditableColumn[];
    data: TEntity[];
    form: FormInstance;
    formListName: string;
    handleSave: (row: TEntity) => void;
    entityName?: string;
    disabledPagination?: boolean;
    isPageJumperEnabled?: boolean;
    isPageSizeChangerEnabled?: boolean;
    tableData?: ITableEntityData;
    metadata?: ITableEntityMeta;
    onChangeTable?: (
        pagination: TablePaginationConfig,
        filters: Record<string, Key[] | null>,
        sorter: any,
        url: string,
    ) => void;
    url?: string;
    loading?: boolean;
    isSelectable?: boolean;
    rowSelection?: TableRowSelection<Record<string, TEntity[]>>;
    linkField?: string;
    dependentLinkField?: string;
    scroll?: any;
    // Используется для модалок загрузки файлов т.к. они хранят файлы со всех страниц
    isIndexRelative?: boolean;
}

export const EditableTableContent: FC<IEditableTableContentProps> = memo(({
    columns,
    data,
    form,
    formListName,
    handleSave,
    entityName,
    disabledPagination,
    isPageJumperEnabled,
    isPageSizeChangerEnabled,
    tableData,
    metadata,
    onChangeTable,
    url,
    loading,
    isSelectable,
    rowSelection,
    linkField,
    dependentLinkField,
    scroll,
    isIndexRelative,
    ...tableProps
}: IEditableTableContentProps) => {
    const {resetTableMode} = useContext(TableModeContext);
    const {setForm: setEditableForm, setValidation: setEditableTableValidation} = useContext(EditableFormTableContext);
    const {setForm: setFileForm, setValidation: setFileTableValidation} = useContext(FormsFileModalContext);

    const setForm = setEditableForm || setFileForm;
    const setValidation = setEditableTableValidation || setFileTableValidation;

    const {
        params,
    } = tableData || {};

    const {
        validateRow,
        getFieldErrors,
        isRowValidating,
        clearRowErrors,
    } = useRowValidation(
        form,
        formListName,
        columns,
        setValidation,
    );

    const [activeKey, setActiveKey] = useState<string | null>(null);

    const tableState = convertFlatObjectToTableState(params as IDefaultTableStateFlat);
    const paginationConfig = useAppSelector(selectDocumentsPagination);
    const totalElements = data?.length;
    const entityClassName = entityName?.split('.').join('-');
    const editableClassNames = cn(
        s['editable-table'],
        {
            [s['editable-table_with-height']]: entityClassName === 'analysis2-org-level',
        }
    );

    const isPaginationDisabled = metadata?.disabledPagination || disabledPagination;
    const isJumperEnabled = metadata?.isPageJumperEnabled || isPageJumperEnabled;
    const isSizeChangerEnabled = metadata?.isPageSizeChangerEnabled || isPageSizeChangerEnabled;

    const tableRowSelection = useMemo(() => {
        if (!isSelectable) return undefined;

        return {
            type: 'checkbox' as const,
            selectedRowKeys: rowSelection?.selectedRowKeys || [],
            onChange: rowSelection?.onChange,
            ...rowSelection,
        };
    }, [isSelectable, rowSelection?.selectedRowKeys, rowSelection?.onChange]);

    const tablePagination: TablePaginationConfig | undefined | false = useEditableTablePagination({
        params,
        isPaginationDisabled,
        isPageJumperEnabled: isJumperEnabled,
        isPageSizeChangerEnabled: isSizeChangerEnabled,
        onChange: () => {
            if (resetTableMode) resetTableMode();
        },
        tableState,
        totalElements,
        paginationConfig,
    });

    const {current: currentPage, pageSize} = tablePagination || {};

    const getRowIndex = (indexOnPage: number) => {
        if (
            isIndexRelative && currentPage && pageSize && !isPaginationDisabled
        ) {
            return indexOnPage + (currentPage - 1) * pageSize;
        }
        return indexOnPage;
    };

    useEffect(() => {
        if (form && setForm) {
            setForm(form);
        }
    }, [form, setForm]);

    const components = useMemo(() => ({
        body: {
            cell: EditableCell,
        },
    }), []);

    const handleCellSave = useCallback(
        async (rowIndex: number, fieldName: string, value: TEntityValue, record: TEntity) => {
            try {
                const relativeRowIndex = getRowIndex(rowIndex);
                // Обновляем значение в форме
                form.setFields([{
                    name: [formListName, relativeRowIndex, fieldName],
                    value,
                }]);

                // Создаем обновленную запись
                const updatedRecord = {
                    ...record,
                    [fieldName]: value
                };

                // Валидируем всю строку и ждем результат
                const validationErrors = await validateRow(relativeRowIndex, updatedRecord);

                // Вызываем внешний handleSave только если нет ошибок
                // Если модалка загрузки файлов, изменять даже если есть ошибки
                const dataHasFiles = record?.file;
                if (Object.keys(validationErrors).length === 0 || dataHasFiles) {
                    handleSave(updatedRecord);
                } else {
                    console.error('Ошибка при валидации, значение не сохранено');
                }
            } catch (err) {
                console.error('Ошибка при сохранении:', err);
            }
        },
        [form, formListName, validateRow, handleSave, currentPage, pageSize]
    );

    const handleFieldChange = useCallback(
        async (rowIndex: number, fieldName: string, value: TEntityValue) => {
            // Получаем текущие данные строки
            const formValues = form.getFieldsValue();
            const relativeRowIndex = getRowIndex(rowIndex);
            const currentRowData = formValues[formListName]?.[relativeRowIndex] || {};

            // Обновляем данные строки
            const updatedRowData = {
                ...currentRowData,
                [fieldName]: value
            };

            // Запускаем валидацию всей строки
            await validateRow(relativeRowIndex, updatedRowData);
        },
        [form, formListName, validateRow, currentPage, pageSize]
    );

    const visibleColumns = useMemo(() => (
        columns?.filter((column: TEditableColumn) => !column?.isHidden)
    ), [columns]);

    const resultColumns = useMemo(() =>
            visibleColumns?.map((column: TEditableColumn, columnIndex) => {
                const leftPosition = calculateFixedColumnLeft(
                    visibleColumns,
                    columnIndex,
                    column.key as string
                );
                const formValues = form.getFieldsValue()[formListName];

                return {
                    ...column,
                    onCell: (record: TEntity, index: number) => {
                        const relativeRowIndex = getRowIndex(index);
                        return {
                            record,
                            rowId: relativeRowIndex,
                            editable: column.editable,
                            alwaysEditable: column.alwaysEditable,
                            required: column.required || column.isRequired,
                            defaultValue: column.defaultValue || record?.[column.key],
                            dataIndex: column.dataIndex,
                            inputType: column.inputType,
                            inputProps: column.inputProps,
                            getFieldState: column.getFieldState,
                            disabled: column.isDisabled,
                            key: [column.key, relativeRowIndex].join(),
                            placeholder: column.placeholder,
                            field: column as unknown as IFieldMeta,
                            handleSave: (fieldName: string, value: TEntityValue) =>
                                handleCellSave(relativeRowIndex, fieldName, value, formValues?.[relativeRowIndex] || {}),
                            generateInputProps: column.generateInputProps,
                            entityName,
                            width: convertWidthData(column.width),
                            left: leftPosition,
                            format: column?.dateFormat,
                            form,
                            formListName,
                            fieldErrors: getFieldErrors(relativeRowIndex, column.dataIndex as string),
                            validating: isRowValidating(relativeRowIndex),
                            onFieldChange: (fieldName: string, value: TEntityValue) =>
                                handleFieldChange(index, fieldName, value),
                            isSelected: rowSelection?.selectedRowKeys?.includes(record?.id as Key),
                            isFocused: [index, column.key].join('-') === activeKey,
                            setActiveKey,
                        } as IEditableCellProps;
                    },
                }
            }),
        [
            visibleColumns,
            form,
            formListName,
            entityName,
            handleCellSave,
            handleFieldChange,
            getFieldErrors,
            isRowValidating,
            rowSelection,
            data,
            activeKey,
            currentPage,
            pageSize,
        ]
    );

    const handleTableChange = (
        pagination: TablePaginationConfig,
        filters: Record<string, Key[] | null>,
        sorter: any,
    ) => {
        if (onChangeTable && url) {
            clearRowErrors();
            onChangeTable(pagination, filters, sorter, url);
        }
    };

    const tableDataSource = useMemo(() => data || [], [data]);

    useEffect(() => () => {
        clearRowErrors();
    }, [clearRowErrors]);

    return (
        <Table
            className={cn(editableClassNames)}
            dataSource={tableDataSource}
            components={components}
            columns={resultColumns as TEditableColumn[]}
            pagination={tablePagination}
            rowKey={linkField || dependentLinkField || 'id'}
            rowSelection={tableRowSelection}
            scroll={scroll ?? {x: 'max-content'}}
            onChange={handleTableChange}
            loading={loading}
            showSorterTooltip={false}
            {...tableProps}
        />
    );
}, (prevProps, nextProps) => (
    isEqual(prevProps.data, nextProps.data) &&
    isEqual(prevProps.rowSelection?.selectedRowKeys, nextProps.rowSelection?.selectedRowKeys) &&
    isEqual(prevProps.columns, nextProps.columns) &&
    prevProps.loading === nextProps.loading
));

export const EditableTable: FC<IEditableTableProps> = ({
    columns,
    dataSource,
    handleSave,
    entityName,
    disabledPagination,
    isPageJumperEnabled,
    isPageSizeChangerEnabled,
    tableData,
    metadata,
    onChangeTable,
    loading,
    url,
    isSelectable,
    rowSelection,
    linkField,
    dependentLinkField,
    scroll,
    isIndexRelative,
    ...props
}: IEditableTableProps) => {
    const [form] = Form.useForm();

    const {
        rows,
    } = tableData || {};
    const data = dataSource || rows;

    return (
        <>
            {/* Форма для управления состоянием */}
            <HiddenForm
                form={form}
                formListName={EDITABLE_TABLE_LIST_NAME}
                data={data || []}
            />

            {/* Таблица */}
            <EditableTableContent
                columns={columns}
                data={data || []}
                form={form}
                formListName={EDITABLE_TABLE_LIST_NAME}
                handleSave={handleSave}
                entityName={entityName}
                disabledPagination={disabledPagination}
                isPageJumperEnabled={isPageJumperEnabled}
                isPageSizeChangerEnabled={isPageSizeChangerEnabled}
                tableData={tableData}
                metadata={metadata}
                onChangeTable={onChangeTable}
                loading={loading}
                url={url}
                isSelectable={isSelectable}
                rowSelection={rowSelection}
                linkField={linkField}
                dependentLinkField={dependentLinkField}
                scroll={scroll}
                isIndexRelative={isIndexRelative}
                {...props}
            />
        </>
    );
};
